// Rich-text helpers for the article body.
//
// Storage format
// --------------
// An article body is still a plain string. Formatting is stored as a tiny,
// allow-listed HTML subset embedded in that string:
//
//   <b>…</b>   <i>…</i>   <span style="color:#rrggbb">…</span>
//   <div style="text-align:center|right">…</div>   (one whole line; left = no tag)
//
// Anything else is escaped. Tags never span a newline, so the existing
// "split on blank lines into paragraphs" logic keeps working.
//
// An article is either "plain" (legacy: no tags, text stored verbatim) or
// "rich" (at least one tag; every `&`, `<` and `>` in the text is escaped).
// Unformatted articles are therefore saved byte-for-byte as before.
//
// The server applies the same allow-list on save (server/src/utils/richText.js)
// and strips the tags for plain-text consumers (mobile feed, push, read-aloud).

const RICH_MARKUP_RE = /<(?:\/?(?:b|i)|span\b[^>]*)>|<div\s+style\s*=\s*"\s*text-align/i;

export const hasRichMarkup = (value) => RICH_MARKUP_RE.test(value || "");

export const escapeText = (value) =>
  String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Reverses escapeText only (order matters: &amp; last).
export const unescapeText = (value) =>
  String(value ?? "").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");

const normalizeTag = (tag) => {
  const lower = tag.toLowerCase();
  const span = /^<span\s+style\s*=\s*"\s*color\s*:\s*(#[0-9a-f]{6})\s*;?\s*">$/.exec(lower);
  if (span) return `<span style="color:${span[1]}">`;
  const div = /^<div\s+style\s*=\s*"\s*text-align\s*:\s*(left|center|right)\s*;?\s*">$/.exec(lower);
  if (div) return `<div style="text-align:${div[1]}">`;
  if (/^<\/?(?:b|i)>$/.test(lower) || lower === "</span>" || lower === "</div>") return lower;
  return null;
};

// Keeps only the allow-listed tags; every other `<` becomes `&lt;`.
export const sanitizeRichText = (value) =>
  String(value ?? "").replace(/<[^<>]*>|</g, (match) => {
    const tag = normalizeTag(match);
    return tag || match.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  });

// Storage string → HTML for the read-only previews. An aligned line is a block
// element, so the newline right after it must not also render as a line break.
export const richTextToHtml = (value) => sanitizeRichText(value).replace(/<\/div>\n/g, "</div>");

// ─── colour handling ─────────────────────────────────────────────────────────

const hexCache = new Map();

// Any CSS colour (named, hex, rgb) → "#rrggbb". The browser does the parsing.
const toHex = (value) => {
  if (!value || typeof document === "undefined") return null;
  if (hexCache.has(value)) return hexCache.get(value);
  const probe = document.createElement("span");
  probe.style.display = "none";
  probe.style.color = value;
  document.body.appendChild(probe);
  const rgb = window.getComputedStyle(probe).color;
  document.body.removeChild(probe);
  const m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(rgb || "");
  const hex = m
    ? `#${[m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, "0")).join("")}`
    : null;
  hexCache.set(value, hex);
  return hex;
};

const isBoldWeight = (weight) => {
  if (!weight) return null;
  if (weight === "bold" || weight === "bolder") return true;
  if (weight === "normal" || weight === "lighter") return false;
  const n = parseInt(weight, 10);
  return Number.isNaN(n) ? null : n >= 600;
};

// ─── DOM → text ──────────────────────────────────────────────────────────────

const BLOCK_TAGS = new Set(["DIV", "P", "LI", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "TR", "CENTER"]);

// Block alignment: "left" | "center" | "right", or null when the element says
// nothing (or says something we do not store, e.g. justify).
const readAlign = (node, style) => {
  if (node.tagName === "CENTER") return "center";
  const raw = String(style.textAlign || (node.getAttribute && node.getAttribute("align")) || "").toLowerCase();
  if (raw === "center") return "center";
  if (raw === "right" || raw === "end") return "right";
  if (raw === "left" || raw === "start") return "left";
  return null;
};

// Walks a DOM tree and returns an ordered list of segments:
//   { text, bold, italic, color, align }  or  { nl: true }
// `dropDefaultColors` ignores pure black/white, used for pasted content where
// they are just the source document's default text colour.
export const collectSegments = (root, { dropDefaultColors = false } = {}) => {
  const segs = [];
  const lastIsNewline = () => segs.length === 0 || segs[segs.length - 1].nl;
  const ensureLineStart = () => {
    if (segs.length > 0 && !lastIsNewline()) segs.push({ nl: true });
  };

  const walk = (node, fmt) => {
    if (node.nodeType === 3) {
      const parts = node.nodeValue.replace(/\u00a0/g, " ").split("\n");
      parts.forEach((part, i) => {
        if (i > 0) segs.push({ nl: true });
        if (part) segs.push({ text: part, ...fmt });
      });
      return;
    }
    if (node.nodeType !== 1) return;

    const tag = node.tagName;
    if (tag === "BR") {
      segs.push({ nl: true });
      return;
    }
    if (["SCRIPT", "STYLE", "META", "TITLE", "HEAD"].includes(tag)) return;

    let next = fmt;
    const style = node.style || {};

    let bold = null;
    if (tag === "B" || tag === "STRONG") bold = true;
    const styleBold = isBoldWeight(style.fontWeight);
    if (styleBold !== null) bold = styleBold; // e.g. Google Docs' <b style="font-weight:normal"> wrapper
    if (bold !== null && bold !== fmt.bold) next = { ...next, bold };

    let italic = null;
    if (tag === "I" || tag === "EM") italic = true;
    if (style.fontStyle === "italic") italic = true;
    else if (style.fontStyle === "normal") italic = false;
    if (italic !== null && italic !== fmt.italic) next = { ...next, italic };

    const rawColor = (tag === "FONT" && node.getAttribute("color")) || style.color;
    if (rawColor) {
      const hex = toHex(rawColor);
      if (hex && !(dropDefaultColors && (hex === "#000000" || hex === "#ffffff"))) {
        if (hex !== fmt.color) next = { ...next, color: hex };
      }
    }

    const isBlock = BLOCK_TAGS.has(tag);
    if (isBlock) {
      const align = readAlign(node, style);
      if (align && align !== fmt.align) next = { ...next, align };
    }
    if (isBlock) ensureLineStart();
    node.childNodes.forEach((child) => walk(child, next));
    if (isBlock) ensureLineStart();
  };

  root.childNodes.forEach((child) =>
    walk(child, { bold: false, italic: false, color: null, align: null })
  );
  return segs;
};

// Alignment is a property of the whole line, so it is not part of run identity.
const sameFormat = (a, b) => a.bold === b.bold && a.italic === b.italic && a.color === b.color;

// One line of text segments → storage string. Adjacent same-format runs are
// merged; inline tags are emitted per run, then an aligned line is wrapped.
const lineToText = (line) => {
  let out = "";
  let run = null;
  const flush = () => {
    if (!run) return;
    let html = escapeText(run.text);
    if (run.text.trim()) {
      if (run.italic) html = `<i>${html}</i>`;
      if (run.bold) html = `<b>${html}</b>`;
      if (run.color) html = `<span style="color:${run.color}">${html}</span>`;
    }
    out += html;
    run = null;
  };
  line.forEach((seg) => {
    if (run && sameFormat(run, seg)) {
      run.text += seg.text;
    } else {
      flush();
      run = { ...seg };
    }
  });
  flush();

  const withText = line.find((seg) => seg.text.trim());
  const align = withText ? withText.align : null;
  if (align === "center" || align === "right") {
    out = `<div style="text-align:${align}">${out}</div>`;
  }
  return out;
};

// Segments → storage string. Tags never span a newline.
export const segmentsToText = (segs) => {
  const lines = [[]];
  segs.forEach((seg) => {
    if (seg.nl) lines.push([]);
    else lines[lines.length - 1].push(seg);
  });
  return lines.map(lineToText).join("\n");
};

// ─── hard line breaks ────────────────────────────────────────────────────────

const ALIGNED_LINE_RE = /^<div style="text-align:(left|center|right)">([\s\S]*)<\/div>$/;
// A line ending like this (optionally followed by closing quotes/brackets)
// finishes a sentence, so the next line is left on its own line.
const SENTENCE_END_RE = /[.!?:।॥][)"'”’»\]]*$/;
const LIST_ITEM_RE = /^\s*(?:[-–—•*·▪●■]|\d+[.)])\s/;

const visibleText = (line) => line.replace(/<[^<>]*>/g, "");

// Text pasted from a printed column (the e-paper, a PDF) has a hard line break
// at the end of every printed line. Shown at any other width those breaks leave
// ragged, short lines. This merges a line into the next one when the first does
// not end a sentence. Blank lines (paragraph breaks), list items and lines with
// a different alignment are never merged, and the merge only ever removes a
// newline, so the worst case is the text staying as it was.
export const joinHardLines = (text) => {
  const parse = (line) => {
    const m = ALIGNED_LINE_RE.exec(line.trim());
    return m ? { align: m[1], inner: m[2] } : { align: "left", inner: line };
  };
  const out = [];
  let prev = null;
  const flush = () => {
    if (!prev) return;
    out.push(prev.align === "left" ? prev.inner : `<div style="text-align:${prev.align}">${prev.inner}</div>`);
    prev = null;
  };

  String(text ?? "")
    .split("\n")
    .forEach((line) => {
      if (!line.trim()) {
        flush();
        out.push("");
        return;
      }
      const cur = parse(line);
      const canJoin =
        prev &&
        prev.align === cur.align &&
        !SENTENCE_END_RE.test(visibleText(prev.inner).trim()) &&
        !LIST_ITEM_RE.test(visibleText(cur.inner));
      if (canJoin) {
        prev.inner = `${prev.inner.replace(/\s+$/, "")} ${cur.inner.replace(/^\s+/, "")}`;
      } else {
        flush();
        prev = cur;
      }
    });
  flush();
  return out.join("\n");
};

// contentEditable element → storage string (always in escaped form).
export const editorToText = (el) => segmentsToText(collectSegments(el));

// Storage string (escaped form) → HTML to seed the editor with.
// An aligned line is a block element: it already ends its own line, so the
// newline after it must not become a <br> (that would add a blank line).
export const textToEditorHtml = (text) =>
  sanitizeRichText(text).replace(/<\/div>\n/g, "</div>").replace(/\n/g, "<br>");

// Pasted clipboard HTML → HTML to insert into the editor.
export const pastedHtmlToEditorHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const text = segmentsToText(collectSegments(doc.body, { dropDefaultColors: true }));
  return textToEditorHtml(joinHardLines(text));
};

export const pastedPlainToEditorHtml = (plain) =>
  textToEditorHtml(joinHardLines(escapeText(String(plain ?? "").replace(/\r\n?/g, "\n"))));
